import { useQuery } from '@apollo/client'
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { Alert, Skeleton } from '@material-ui/lab'
import Pagination from '@material-ui/lab/Pagination'
import { useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import PromotionTabs from '../../components/PromotionTabs'
import {
  PROMOTIONS_STATS_QUERY,
  PromotionsStatsData
} from '../../graphql/promotions'
import { formatCount } from '../../helpers/promotionAmount'
import { PERIOD_PRESETS, windowFor } from '../../helpers/promotionPeriod'
import {
  DEFAULT_PROMOTION_SORT,
  PROMOTION_SORT_OPTIONS,
  promotionSortFor
} from '../../helpers/promotionStatsSort'
import { getTranslate, lang } from '../../localization'
import OverviewCharts from './OverviewCharts'
import OverviewTable, { STATUS_LABEL } from './OverviewTable'
import OverviewTableMobile from './OverviewTableMobile'
import OverviewTotals from './OverviewTotals'
import {
  DEFAULT_PROMOTION_STATUS_FILTER,
  PROMOTION_STATUS_FILTERS,
  promotionStatusFilterFor
} from './statusFilter'
import useStyles from './styles/index.styles'

const PER_PAGE = 20

/**
 * The window bound, in the viewer's calendar.
 *
 * `toLocaleDateString()` with NO argument uses the BROWSER's locale, not the app's — so a
 * Persian UI rendered a Gregorian date in whatever format the browser preferred, which an
 * operator has to convert mentally before they can tell whether the window is the one they
 * picked. The portal's fuller Jalali helpers in helpers/date.ts are shaped for pickers rather
 * than a single label, so the locale is passed explicitly here.
 */
const formatWindowDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-GB') : ''

/**
 * Cross-promotion performance — the answer to "which campaigns are working".
 *
 * A PEER VIEW of the promotion list, which is what makes it a tab. Evaluation Trace remains
 * contextual to Why not applied: its subject is a booking, while this screen's subject is
 * promotions seen in aggregate rather than as a list.
 *
 * Everything money-shaped here obeys one server-side constraint: there is no scalar grand
 * total, because discountMinor is minor units of a per-promotion currency and the scale
 * differs by currency. See OverviewTotals.
 */
const PromotionsOverview = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [periodKey, setPeriodKey] = useState('all')
  // Reads the shared default rather than repeating it. It was the literal 'newest', which
  // meant reordering PROMOTION_SORT_OPTIONS changed the dropdown and nothing else: the screen
  // still opened on creation date, which is the whole complaint. The constant and the screen
  // have to be the same fact, not two facts that happen to agree.
  const [sortKey, setSortKey] = useState(DEFAULT_PROMOTION_SORT.key)
  // Opens on Active — see statusFilter.ts for why that default is a judgement call and not
  // an obvious one. The screen holds the KEY, and the argument sent to the server is derived
  // from it, so the "any status" sentinel never has to be understood by two places at once.
  const [statusKey, setStatusKey] = useState(DEFAULT_PROMOTION_STATUS_FILTER)
  const [page, setPage] = useState(1)

  const period = useMemo(
    () => PERIOD_PRESETS.find((p) => p.key === periodKey) ?? PERIOD_PRESETS[0],
    [periodKey]
  )
  const window = useMemo(() => windowFor(period.days), [period])
  const sort = useMemo(() => promotionSortFor(sortKey), [sortKey])
  const status = useMemo(() => promotionStatusFilterFor(statusKey), [statusKey])

  const { data, loading, error, refetch } = useQuery<PromotionsStatsData>(
    PROMOTIONS_STATS_QUERY,
    {
      variables: {
        from: window.from,
        to: window.to,
        page,
        perPage: PER_PAGE,
        orderBy: sort.orderBy,
        descending: sort.descending,
        // undefined, not null — Apollo omits an undefined variable entirely, which is what
        // "every status" has to be on the wire. Sending null would still transmit the
        // variable, and the resolver distinguishes an absent field from a present one.
        status
      },
      // The figures are counters that move as bookings land. A cached page from five minutes
      // ago read as current is the failure this screen exists to avoid.
      fetchPolicy: 'cache-and-network'
    }
  )

  const stats = data?.promotionsStats
  const pageCount = stats
    ? Math.ceil(stats.total / (stats.perPage || PER_PAGE))
    : 0

  return (
    <div>
      <PageHeader title={getTranslate('Promotion overview')} />
      {/*
        THE h1 IS VISUALLY HIDDEN — see PromotionSkipReasons for the full reasoning. Short
        version: Notifications records trying a visible page title above a tab strip and
        reverting it, and two other tabbed screens render none, so a visible one here would
        diverge from three screens that already settled this. A heading-navigation user still
        gets an h1 naming the screen, which the tab strip cannot give them.
      */}
      <Typography variant="srOnly" component="h1">
        {getTranslate('Promotion overview')}
      </Typography>
      <PromotionTabs />

      <div className={classes.controls}>
        <FormControl
          variant="outlined"
          size="small"
          className={classes.filterSelect}>
          <InputLabel id="promotion-overview-period">
            {getTranslate('Period')}
          </InputLabel>
          <Select
            labelId="promotion-overview-period"
            id="promotion-overview-period-select"
            value={periodKey}
            label={getTranslate('Period')}
            data-cy="OverviewPeriodSelect"
            onChange={(event) => {
              setPeriodKey(String(event.target.value))
              // Back to page one. Page 4 of a 12-month view is very unlikely to be page 4 of
              // a 30-day one, and silently keeping the number shows an empty page that reads
              // as "no data" rather than "you are past the end".
              setPage(1)
            }}>
            {PERIOD_PRESETS.map((preset) => (
              <MenuItem key={preset.key} value={preset.key}>
                {getTranslate(preset.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          size="small"
          className={classes.filterSelect}>
          <InputLabel id="promotion-overview-sort">
            {getTranslate('Sort by')}
          </InputLabel>
          <Select
            labelId="promotion-overview-sort"
            id="promotion-overview-sort-select"
            value={sortKey}
            label={getTranslate('Sort by')}
            data-cy="OverviewSortSelect"
            onChange={(event) => {
              setSortKey(String(event.target.value))
              // Back to page one, for a sharper reason than the period control's. Ordering
              // does not change WHICH promotions match — the set and the total are identical
              // — it changes only their arrangement. So page 4 of "most applied" holds a
              // completely different twenty rows from page 4 of "newest first", and keeping
              // the number silently shows unrelated rows under an unchanged page indicator.
              // There is no empty page to hint that anything moved.
              setPage(1)
            }}>
            {PROMOTION_SORT_OPTIONS.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {getTranslate(option.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          size="small"
          className={classes.filterSelect}>
          <InputLabel id="promotion-overview-status">
            {getTranslate('Status')}
          </InputLabel>
          <Select
            labelId="promotion-overview-status"
            id="promotion-overview-status-select"
            value={statusKey}
            label={getTranslate('Status')}
            data-cy="OverviewStatusSelect"
            onChange={(event) => {
              setStatusKey(String(event.target.value))
              // Back to page one, for the period control's reason rather than the sort
              // control's: this changes WHICH promotions match, so the set and the total
              // both move. Page 4 of Active is very unlikely to exist at all under Draft.
              setPage(1)
            }}>
            {PROMOTION_STATUS_FILTERS.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {getTranslate(option.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/*
          The window the SERVER applied, echoed — not the one requested.

          A period figure and a lifetime figure are indistinguishable from the numbers alone,
          and they do not merely differ in range: the applied figures change SOURCE with the
          window. Unwindowed they come from the usage counter that cap enforcement reads;
          windowed, from an aggregate over confirmed reservations, because a running counter
          cannot answer "in September". The counter is decremented on release and the period
          aggregate never sees a released row, so the two are not reconcilable by arithmetic
          and must never be presented side by side as if they were.
        */}
        {stats && (
          <Typography
            variant="body2"
            className={classes.windowNote}
            data-cy="OverviewWindow">
            {/* Says ACROSS ALL PROMOTIONS explicitly. The totals count every promotion the
                filter matches, while the table below shows one page of them, so the two
                disagree by design — 15 applied above a page whose rows sum to 4 reads as a
                bug unless the caption says which population each is counting. */}
            {/*
              NAMES THE STATUS when one is filtered. The caption's whole job is to say which
              population the totals count, and a status filter changes that population — so
              "Across all promotions" above a figure covering only the Active ones is not an
              omission, it is a false statement, and the screen defaults to filtering. The
              status is interpolated rather than appended so a translator can place it.
            */}
            {status
              ? stats.periodFrom
                ? getTranslate(
                    'Across all {status} promotions, counted {from} – {to}',
                    {
                      status: getTranslate(STATUS_LABEL[status] ?? status),
                      from: formatWindowDate(stats.periodFrom),
                      to: formatWindowDate(stats.periodTo)
                    }
                  )
                : getTranslate(
                    'Across all {status} promotions, over the whole life of each promotion',
                    { status: getTranslate(STATUS_LABEL[status] ?? status) }
                  )
              : stats.periodFrom
              ? getTranslate('Across all promotions, counted {from} – {to}', {
                  from: formatWindowDate(stats.periodFrom),
                  to: formatWindowDate(stats.periodTo)
                })
              : getTranslate(
                  'Across all promotions, over the whole life of each promotion'
                )}
          </Typography>
        )}
      </div>

      {/*
        The figures changed, announced.

        Changing the period swaps every number while focus stays on the Select, and the
        background refetch replaces cached figures with fresh ones — both silently. A
        screen-reader user selects "Last 30 days", hears the option name, and has no
        indication anything happened.

        Polite: the reader asked for this and is waiting, but it must not interrupt.

        IT DOES NOT CARRY THE WINDOW, and this comment claimed it did — inherited, not
        introduced here, but false since the string was written: only `shown`, `total`,
        `status` and `sort` are interpolated, and the dates live in the non-live windowNote
        paragraph below. So a screen-reader user who changes the Period hears new counts and
        is never told whether they are a lifetime or a month, which is precisely the
        distinction this screen's own docblock calls the most important thing on it. Filed
        rather than fixed here: doing it properly means the window in every announcement
        variant, and this change already added one axis to that string.

        AND THE ORDERING, because changing the sort is the one action here that changes
        nothing this sentence would otherwise mention. The set and the totals are identical —
        only the arrangement moves — so without the sort named, the announced string is
        byte-identical before and after and the region is silent exactly when it is needed.

        AND THE STATUS, for a weaker version of the same reason. A status change usually does
        move the counts, so the string usually differs on its own — but two statuses with the
        same number of promotions produce a byte-identical sentence and the identical silence.
        "Usually announces" is not a property worth relying on when naming the filter costs
        one interpolation.

        TWO STRINGS, not one with a placeholder that collapses to empty. Collapsing leaves a
        double space, and — worse — it assumes the status word sits before "promotions" with
        a space between, which is a claim about English word order that a translator cannot
        undo from inside the placeholder. The unfiltered string is also the EXISTING key,
        untouched, so its translation keeps working.
      */}
      <div role="status" className={classes.announcement}>
        {loading
          ? getTranslate('Loading the overview…')
          : stats
          ? status
            ? getTranslate(
                'Showing {shown} of {total} {status} promotions, sorted by {sort}',
                {
                  shown: formatCount(stats.rows.length),
                  total: formatCount(stats.total),
                  status: getTranslate(STATUS_LABEL[status] ?? status),
                  sort: getTranslate(sort.label)
                }
              )
            : getTranslate(
                'Showing {shown} of {total} promotions, sorted by {sort}',
                {
                  shown: formatCount(stats.rows.length),
                  total: formatCount(stats.total),
                  sort: getTranslate(sort.label)
                }
              )
          : ''}
      </div>

      {error && (
        <Alert
          severity="error"
          className={classes.error}
          data-cy="OverviewError"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {getTranslate('Retry')}
            </Button>
          }>
          {/* The copy said "Try again" with no control to do it — telling someone to act and
              giving them nothing to act with. */}
          {getTranslate('The overview could not be loaded.')}
        </Alert>
      )}

      {/*
        Skeletons, not zeroes. A zero is a real and meaningful figure on this screen — a
        campaign nobody used — so rendering zeroes while loading states something false.
      */}
      <div aria-busy={loading}>
        {loading && !stats ? (
          <>
            <Skeleton variant="rect" height={120} data-cy="OverviewSkeleton" />
            <Skeleton variant="rect" height={240} style={{ marginTop: 16 }} />
          </>
        ) : (
          stats && (
            <>
              <OverviewTotals
                totalAppliedCount={stats.totalAppliedCount}
                totalInFlightCount={stats.totalInFlightCount}
                discountTotals={stats.discountTotals}
                inFlightDiscountTotals={stats.inFlightDiscountTotals}
              />
              {/* The pair every list screen in this portal ships. The five-column grid gives
                each numeric column ~60px at 360px, where a money figure breaks mid-token
                across three lines. */}
              {/* Charts ABOVE the table, and the table is still the complete view: the charts
                  cap at the top eight promotions, and they are aria-hidden because the table
                  carries the same figures with real semantics. A reader who wants the shape
                  gets it first; a reader who wants the numbers scrolls one section. */}
              <OverviewCharts rows={stats.rows} />
              {/* The same sentence the live region gives a screen reader, rendered where a
                  sighted reader is when the question arises. The totals card counts every
                  promotion the filter matches; the table shows one page — so "15 applied"
                  above a page summing to 4 reads as a bug, and the caption saying so sits two
                  sections up and off-screen by the time anyone reaches the rows. The screen
                  reader was being told something the operator who reported this was not. */}
              <Typography
                variant="body2"
                className={classes.tableScope}
                data-cy="OverviewTableScope">
                {/*
                  Names the status for the same reason the totals caption does. This one sits
                  directly above the rows, so an unfiltered claim here is the most immediately
                  checkable falsehood on the screen: it would say "of 12 promotions" over a
                  list that is 12 Active ones out of 30.
                */}
                {status
                  ? getTranslate(
                      'Showing {shown} of {total} {status} promotions',
                      {
                        shown: formatCount(stats.rows.length),
                        total: formatCount(stats.total),
                        status: getTranslate(STATUS_LABEL[status] ?? status)
                      }
                    )
                  : getTranslate('Showing {shown} of {total} promotions', {
                      shown: formatCount(stats.rows.length),
                      total: formatCount(stats.total)
                    })}
              </Typography>
              {isSmallScreen ? (
                <OverviewTableMobile rows={stats.rows} />
              ) : (
                <OverviewTable rows={stats.rows} />
              )}
              {pageCount > 1 && (
                <div className={classes.paginationContainer}>
                  <Pagination
                    count={pageCount}
                    color="primary"
                    size={isSmallScreen ? 'small' : 'medium'}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                  />
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  )
}

export default PromotionsOverview
